"use strict";
/**
 * API Response Mappers
 *
 * Maps API response field names to React model field names.
 * The backend API uses different naming conventions (e.g. orgId, cohortId, termId)
 * than our React models (which use id consistently).
 *
 * Reference: skylab-web/src/app/core/models/*.model.ts fromJSON() methods
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.mapEverspringProgramMapping = exports.mapDegreePlan = exports.mapProfile = exports.mapCourse = exports.mapTerm = exports.mapCohort = exports.mapAcademicProgram = exports.mapOrganization = void 0;
const text_1 = require("./text");
/* eslint-disable @typescript-eslint/no-explicit-any */
const mapOrganization = (raw) => ({
    id: raw.orgId || raw.id,
    name: raw.name || '',
    tenantId: raw.tenantId || '',
    active: raw.active,
    descriptions: Array.isArray(raw.description)
        ? raw.description.map((d) => ({ orgDescType: d.orgDescType || '', description: d.description || '' }))
        : undefined,
    academicCalendarLink: raw.academicCalendarLink || undefined,
    academicCalendarDescription: raw.academicCalendarDescription || undefined,
    courseDescriptionsLink: raw.courseDescriptionsLink || undefined,
});
exports.mapOrganization = mapOrganization;
const mapAcademicProgram = (raw) => {
    var _a;
    return ({
        id: raw.academicProgramId || raw.id,
        name: raw.name || '',
        code: raw.code || '',
        tenantId: raw.tenantId || '',
        organizationId: raw.offeredbyOrgId || raw.organizationId || '',
        active: (_a = raw.approved) !== null && _a !== void 0 ? _a : raw.active,
    });
};
exports.mapAcademicProgram = mapAcademicProgram;
const mapCohort = (raw) => ({
    id: raw.cohortId || raw.id,
    name: raw.name || '',
    tenantId: raw.tenantId || '',
    organization: raw.organizationId
        ? { id: raw.organizationId, name: '', tenantId: raw.tenantId || '' }
        : raw.organization
            ? (0, exports.mapOrganization)(raw.organization)
            : { id: '', name: '', tenantId: '' },
    academicProgram: raw.academicProgram
        ? (0, exports.mapAcademicProgram)(raw.academicProgram)
        : undefined,
    program: raw.program,
    specialization: raw.specialization,
    track: raw.track,
    startTermId: raw.startTermId,
    active: raw.active,
});
exports.mapCohort = mapCohort;
const mapTerm = (raw) => ({
    id: raw.termId || raw.id,
    name: raw.internalName || raw.name || '',
    clientName: raw.clientName,
    startDate: raw.startDate ? new Date(raw.startDate) : new Date(),
    endDate: raw.endDate ? new Date(raw.endDate) : new Date(),
    tenantId: raw.tenantId || '',
    academicYear: raw.academicYear,
    sfTermName: raw.sfTermName,
    confirmedDate: raw.confirmedDate,
});
exports.mapTerm = mapTerm;
const mapCourse = (raw) => ({
    id: raw.courseId || raw.id,
    code: raw.code || raw.displayCode || '',
    name: (0, text_1.decodeEncodedAmpersands)(raw.displayName || raw.longDescription || raw.shortDescription || raw.name) || '',
    credits: raw.credits || 0,
    tenantId: raw.tenantId || '',
    description: raw.description,
    displayCode: (0, text_1.decodeEncodedAmpersands)(raw.displayCode),
    displayName: (0, text_1.decodeEncodedAmpersands)(raw.displayName),
    shortDescription: (0, text_1.decodeEncodedAmpersands)(raw.shortDescription),
    longDescription: (0, text_1.decodeEncodedAmpersands)(raw.longDescription),
    approved: raw.approved,
    repeatable: raw.repeatable,
});
exports.mapCourse = mapCourse;
const mapProfile = (raw) => {
    var _a;
    // The API profile has a complex structure with typed arrays for names/emails
    let firstName = raw.firstName || '';
    let lastName = raw.lastName || '';
    let email = raw.email || '';
    // Handle name array format: [{type: 'primary', firstName: '...', lastName: '...'}]
    if (Array.isArray(raw.name)) {
        const primary = raw.name.find((n) => n.type === 'primary') || raw.name[0];
        if (primary) {
            firstName = primary.firstName || firstName;
            lastName = primary.lastName || lastName;
        }
    }
    // Handle emailAddress array format: [{type: 'university', emailAddress: '...'}, ...]
    if (Array.isArray(raw.emailAddress)) {
        const universityEmail = raw.emailAddress.find((e) => e.type === 'university');
        const primaryEmail = raw.emailAddress.find((e) => e.primary);
        const firstEmail = raw.emailAddress[0];
        email = ((_a = (universityEmail || primaryEmail || firstEmail)) === null || _a === void 0 ? void 0 : _a.emailAddress) || email;
    }
    return {
        id: raw.profileId || raw.id,
        userId: raw.userId || '',
        tenantId: raw.tenantId || '',
        firstName,
        lastName,
        email,
        studentId: raw.studentId,
    };
};
exports.mapProfile = mapProfile;
const mapDegreePlanTermCourse = (raw) => {
    const courseData = raw.degreePlanTermCourse || raw;
    return {
        course: courseData.course
            ? (0, exports.mapCourse)(courseData.course)
            : { id: courseData.courseId || '', code: '', name: '', credits: 0, tenantId: '' },
        failed: courseData.failed || false,
    };
};
const mapDegreePlanTerm = (raw) => {
    var _a;
    const termData = raw.degreePlanTerm || raw;
    return {
        id: termData.degreePlanTermId || termData.id || '',
        term: termData.term
            ? (0, exports.mapTerm)(termData.term)
            : { id: termData.termId || '', name: '', startDate: new Date(), endDate: new Date(), tenantId: '' },
        position: (_a = termData.position) !== null && _a !== void 0 ? _a : 0,
        courses: Array.isArray(raw.degreePlanTermCourseDtos)
            ? raw.degreePlanTermCourseDtos.map(mapDegreePlanTermCourse)
            : Array.isArray(termData.courses)
                ? termData.courses.map(mapDegreePlanTermCourse)
                : [],
    };
};
const mapDegreePlan = (raw) => {
    // The API wraps plan data in a degreePlan nested object
    const plan = raw.degreePlan || raw;
    return {
        id: plan.degreePlanId || plan.id || '',
        type: plan.type || 'student',
        tenantId: plan.tenantId || '',
        studentProgramId: plan.studentProgramId,
        offSequence: plan.offSequence || false,
        graduationDate: plan.graduationDate ? new Date(plan.graduationDate) : undefined,
        finalActiveTermId: plan.finalActiveTermId,
        cohort: plan.cohortId
            ? { id: plan.cohortId, name: '', tenantId: plan.tenantId || '', organization: { id: '', name: '', tenantId: '' } }
            : plan.cohort
                ? (0, exports.mapCohort)(plan.cohort)
                : { id: '', name: '', tenantId: '', organization: { id: '', name: '', tenantId: '' } },
        organization: plan.organizationId
            ? { id: plan.organizationId, name: '', tenantId: plan.tenantId || '' }
            : plan.organization
                ? (0, exports.mapOrganization)(plan.organization)
                : { id: '', name: '', tenantId: '' },
        academicProgram: plan.academicProgramId
            ? { id: plan.academicProgramId, name: '', code: '', tenantId: plan.tenantId || '', organizationId: '' }
            : plan.academicProgram
                ? (0, exports.mapAcademicProgram)(plan.academicProgram)
                : undefined,
        profile: plan.profileId
            ? { id: plan.profileId, userId: '', tenantId: plan.tenantId || '', firstName: '', lastName: '' }
            : plan.profile
                ? (0, exports.mapProfile)(plan.profile)
                : undefined,
        parentDegreePlan: plan.parentDegreePlanId
            ? { id: plan.parentDegreePlanId }
            : undefined,
        audit: plan.audit
            ? {
                dateCreated: plan.audit.dateCreated,
                dateModified: plan.audit.dateModified,
                transactionId: plan.audit.transactionId,
                userCreatedBy: plan.audit.userCreatedBy,
                userModifiedBy: plan.audit.userModifiedBy,
            }
            : undefined,
        terms: Array.isArray(raw.degreePlanTermDtos)
            ? raw.degreePlanTermDtos.map(mapDegreePlanTerm)
            : Array.isArray(plan.terms)
                ? plan.terms.map(mapDegreePlanTerm)
                : [],
    };
};
exports.mapDegreePlan = mapDegreePlan;
const mapEverspringProgramMapping = (raw) => ({
    id: raw.everspringProgramMappingId || raw.id,
    academicProgramId: raw.academicProgramId || '',
    program: raw.program || '',
    specialization: raw.specialization,
    track: raw.track,
});
exports.mapEverspringProgramMapping = mapEverspringProgramMapping;
/* eslint-enable @typescript-eslint/no-explicit-any */
